import { Autocomplete, Typography, TextField, Stack } from '@mui/material'
import { type ReactElement, useCallback, useEffect, useState } from 'react'

import { type ClientModel } from 'state/client'

import { type AppState } from 'state/index'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'

import { assignSelectedClient } from 'actions/client'

import { assignSelectedFarms } from 'actions/farm'

import { fetchBBox } from 'api/field'

import { setDefaultExtent } from 'actions/field'
import { TEST_IDS } from 'test-ids'

export type ClientSelectorProps = {
  selectedClient?: ClientModel | null
  onChange?: Function
  hideLabel?: boolean
  compact?: boolean
}
export default function ClientSelector(
  props: ClientSelectorProps,
): ReactElement {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const clients: ClientModel[] = useSelector(
    (state: AppState) => state.client.collection,
  )
  const year = useSelector((state: AppState) => state.field.year)
  const clientsWithBoundaries: { [key: number | string]: number[] } =
    useSelector((state: AppState) => state.client.clientFieldFeaturesFetched)
  const [selectedClient, setSelectedClient] = useState<ClientModel | null>(null)
  useEffect(() => {
    if (props.selectedClient !== undefined) {
      setSelectedClient(props.selectedClient ?? null)
    }
  }, [props.selectedClient])

  const onClientChange = useCallback(
    (event, newValue, reason) => {
      if (newValue) {
        //@ts-expect-error
        dispatch(fetchBBox({ clientId: newValue.id, year }))
        if (clientsWithBoundaries[newValue.id] !== undefined) {
          dispatch(setDefaultExtent(clientsWithBoundaries[newValue.id]))
        }
      }
      if (props.onChange) {
        props.onChange(event, newValue, reason)
      } else {
        let client, farms
        if (reason === 'clear') {
          client = ''
          farms = []
        } else {
          client = newValue
          farms = newValue?.['farms'] || []
        }
        localStorage.setItem('selectedClient', JSON.stringify(client))
        dispatch(assignSelectedClient(client))
        setSelectedClient(client)
        localStorage.setItem('selectedFarms', JSON.stringify(farms))
        dispatch(assignSelectedFarms(farms))
      }
    },
    [props.onChange, clientsWithBoundaries, year],
  )
  return (
    <Stack spacing={props?.compact ? 0.5 : 1}>
      {props?.hideLabel ? null : (
        <Typography variant={props?.compact ? 'caption' : undefined}>
          {t('clients') + (props?.compact ? '' : ':')}
        </Typography>
      )}
      <Autocomplete
        fullWidth
        style={props?.compact ? undefined : { maxHeight: '40px' }}
        sx={
          props?.compact
            ? undefined
            : {
                '& input': {
                  height: '1em',
                },
                '& label': {
                  lineHeight: '1.5em',
                  fontSize: '1em',
                },
              }
        }
        id={TEST_IDS.clients}
        data-testid={TEST_IDS.clients}
        autoHighlight
        isOptionEqualToValue={(option, value) => option.id === value.id}
        size="small"
        options={clients}
        selectOnFocus
        getOptionLabel={(option) => option?.name || ''}
        value={selectedClient ?? null}
        onChange={(event, newValue, reason) => {
          onClientChange(event, newValue, reason)
        }}
        renderInput={(params) => {
          return props.hideLabel ? (
            //@ts-ignore
            <TextField
              {...params}
              label={t('clients')}
              placeholder={t('clients')}
              style={{ width: '100%' }}
            />
          ) : (
            //@ts-ignore
            <TextField
              {...params}
              placeholder={t('clients')}
              style={{ width: '100%' }}
            />
          )
        }}
      />
    </Stack>
  )
}
